import React, { useEffect, useState } from 'react';
import { List, Datagrid, TextField, DateField, useRecordContext, useDataProvider, ReferenceField, NumberField, useTheme, useNotify, TopToolbar } from 'react-admin';
import { Button, CircularProgress, Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SyncIcon from '@mui/icons-material/Sync';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { API_URL } from './apiConfig';

const today = new Date().toISOString().split('T')[0];
const todayDate = new Date(today);

const AtPropertyOrArrivalField = () => {
    const record = useRecordContext();
    if (!record) return null;
    const checkIn = record.check_in;
    const checkOut = record.check_out;
    if (checkIn && checkOut && checkIn <= today && today <= checkOut) {
        const checkOutDate = new Date(checkOut);
        const diffTime = checkOutDate - todayDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return (
            <span style={{ color: 'green', fontWeight: 'bold' }}>
                Current ({diffDays} days left)
            </span>
        );
    }
    if (checkIn) {
        const checkInDate = new Date(checkIn);
        const diffTime = checkInDate - todayDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 0) {
            return (
                <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                    {diffDays} days to arrival
                </span>
            );
        } else if (diffDays === 0) {
            return (
                <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                    Arriving today
                </span>
            );
        }
    }
    return null;
};

// Custom field to calculate number of nights
const NightsField = () => {
    const record = useRecordContext();
    if (!record) return null;
    const { check_in, check_out } = record;
    if (!check_in || !check_out) return null;
    const checkInDate = new Date(check_in);
    const checkOutDate = new Date(check_out);
    const diffTime = checkOutDate - checkInDate;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return <span>{diffDays}</span>;
};

const BookingFutureListActions = () => (
    <TopToolbar>
        <SyncBookingComButton />
    </TopToolbar>
);

const BookingFutureList = props => (
    <List
        {...props}
        resource="bookings"
        filter={{ check_out_gte: today }}
        sort={{ field: 'check_in', order: 'ASC' }}
        title="Upcoming Bookings"
        actions={<BookingFutureListActions />}
    >
        <Datagrid rowClick="edit">
            <TextField source="id" />
            <TextField source="status" />
            <TextField source="source" />
            <DateField source="check_in" />
            <DateField source="check_out" />
            <NumberField source="num_guests" />
            <NumberField source="price" options={{ style: 'currency', currency: 'PLN' }} label="Brutto" />
            <NumberField source="commission" options={{ style: 'currency', currency: 'PLN' }} label="Prowizja" />
            <NumberField source="total" options={{ style: 'currency', currency: 'PLN' }} label="Netto" />
            <NightsField label="Nights" />
            <ReferenceField source="guest" reference="guests" link="show">
                <TextField source="name" />
            </ReferenceField>
            <ReferenceField source="apartment" reference="apartments">
                <TextField source="name" />
            </ReferenceField>
            <AtPropertyOrArrivalField label="At Property / Arrival" />
        </Datagrid>
    </List>
);

const DashboardSummary = () => {
    const dataProvider = useDataProvider();
    const [stats, setStats] = useState({ bookings: 0, guests: 0 });
    const [income, setIncome] = useState({ current: 0, next: 0 });
    const [todayStats, setTodayStats] = useState({ arrivals: 0, departures: 0 });
    const [theme] = useTheme();
    const isDarkMode = theme === 'dark';

    // Calculate current and next month date ranges
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextMonthEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0, 23, 59, 59, 999);

    // Format YYYY-MM-DD - ensure we get the correct date
    const fmt = d => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        dataProvider.getList('bookings', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'ASC' },
            filter: { check_in_lte: today, check_out_gte: today },
        }).then(({ data }) => {
            setStats({
                bookings: data.length,
                guests: data.reduce((sum, b) => sum + (b.num_guests || 0), 0),
            });
        });

        // Arrivals today
        dataProvider.getList('bookings', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'ASC' },
            filter: { check_in: today },
        }).then(({ data }) => {
            setTodayStats(s => ({ ...s, arrivals: data.length }));
        });

        // Departures today
        dataProvider.getList('bookings', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'ASC' },
            filter: { check_out: today },
        }).then(({ data }) => {
            setTodayStats(s => ({ ...s, departures: data.length }));
        });

        // Current month income
        dataProvider.getList('bookings', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'ASC' },
            filter: {
                check_out_gte: fmt(currentMonthStart),
                check_out_lte: fmt(currentMonthEnd),
            },
        }).then(({ data }) => {
            setIncome(income => ({ ...income, current: data.reduce((sum, b) => sum + (b.total || 0), 0) }));
        });

        // Next month income
        dataProvider.getList('bookings', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'ASC' },
            filter: {
                check_out_gte: fmt(nextMonthStart),
                check_out_lte: fmt(nextMonthEnd),
            },
        }).then(({ data }) => {
            setIncome(income => ({ ...income, next: data.reduce((sum, b) => sum + (b.total || 0), 0) }));
        });
    }, [dataProvider]);

    // Month names
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const currentMonthName = monthNames[now.getMonth()];
    const nextMonthName = monthNames[(now.getMonth() + 1) % 12];

    // Define colors for light and dark modes
    const colors = {
        bookings: isDarkMode ? { bg: '#1e3a5f', text: '#e3f2fd' } : { bg: '#e3f2fd', text: '#1565c0' },
        guests: isDarkMode ? { bg: '#1b4332', text: '#e8f5e9' } : { bg: '#e8f5e9', text: '#2e7d32' },
        arrivals: isDarkMode ? { bg: '#4a1c3d', text: '#fce4ec' } : { bg: '#fce4ec', text: '#c2185b' },
        departures: isDarkMode ? { bg: '#004d5c', text: '#e0f7fa' } : { bg: '#e0f7fa', text: '#00838f' },
        currentIncome: isDarkMode ? { bg: '#4a3c28', text: '#fff3e0' } : { bg: '#fff3e0', text: '#e65100' },
        nextIncome: isDarkMode ? { bg: '#3d3360', text: '#ede7f6' } : { bg: '#ede7f6', text: '#5e35b1' }
    };

    return (
        <div style={{ display: 'flex', gap: 32, marginBottom: 24 }}>
            <div style={{ 
                background: colors.bookings.bg, 
                color: colors.bookings.text,
                padding: 16, 
                borderRadius: 8, 
                minWidth: 120 
            }}>
                <div style={{ fontSize: 18, fontWeight: 'bold' }}>{stats.bookings}</div>
                <div>Current Bookings</div>
            </div>
            <div style={{ 
                background: colors.guests.bg, 
                color: colors.guests.text,
                padding: 16, 
                borderRadius: 8, 
                minWidth: 120 
            }}>
                <div style={{ fontSize: 18, fontWeight: 'bold' }}>{stats.guests}</div>
                <div>People at Property</div>
            </div>
            <div style={{ 
                background: colors.arrivals.bg, 
                color: colors.arrivals.text,
                padding: 16, 
                borderRadius: 8, 
                minWidth: 120 
            }}>
                <div style={{ fontSize: 18, fontWeight: 'bold' }}>{todayStats.arrivals}</div>
                <div>Arrivals Today</div>
            </div>
            <div style={{ 
                background: colors.departures.bg, 
                color: colors.departures.text,
                padding: 16, 
                borderRadius: 8, 
                minWidth: 120 
            }}>
                <div style={{ fontSize: 18, fontWeight: 'bold' }}>{todayStats.departures}</div>
                <div>Departures Today</div>
            </div>
            <div style={{ 
                background: colors.currentIncome.bg, 
                color: colors.currentIncome.text,
                padding: 16, 
                borderRadius: 8, 
                minWidth: 160 
            }}>
                <div style={{ fontSize: 18, fontWeight: 'bold' }}>{income.current.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</div>
                <div>Expected Income<br />{currentMonthName}</div>
            </div>
            <div style={{ 
                background: colors.nextIncome.bg, 
                color: colors.nextIncome.text,
                padding: 16, 
                borderRadius: 8, 
                minWidth: 160 
            }}>
                <div style={{ fontSize: 18, fontWeight: 'bold' }}>{income.next.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</div>
                <div>Expected Income<br />{nextMonthName}</div>
            </div>
        </div>
    );
};

const SyncBookingComButton = () => {
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    const handleSync = async () => {
        setLoading(true);
        try {
            // Get auth token from localStorage (matches authProvider)
            const token = localStorage.getItem('access_token');
            const headers = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await fetch(`${API_URL}/sync-booking-com/`, {
                method: 'POST',
                headers,
            });
            const data = await response.json();
            if (data.success) {
                notify('Booking.com sync completed successfully', { type: 'success' });
                // Refresh the bookings list after a short delay
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                notify(`Sync failed: ${data.message}`, { type: 'error' });
            }
        } catch (error) {
            notify(`Sync failed: ${error.message}`, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSync}
            disabled={loading}
            sx={{ marginLeft: '8px' }}
        >
            {loading ? 'Syncing...' : 'Sync Booking.com'}
        </Button>
    );
};

// Custom Tooltip with month name
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <Box sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                border: '1px solid #ccc', 
                padding: '12px',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
                    Miesiąc: {label}
                </Typography>
                {payload.map((entry, index) => (
                    <Typography key={index} variant="body2" sx={{ color: entry.color, mb: 0.5 }}>
                        {entry.name}: {entry.value.toLocaleString('pl-PL')}
                    </Typography>
                ))}
            </Box>
        );
    }
    return null;
};

const CustomCurrencyTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <Box sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                border: '1px solid #ccc', 
                padding: '12px',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
                    Miesiąc: {label}
                </Typography>
                {payload.map((entry, index) => (
                    <Typography key={index} variant="body2" sx={{ color: entry.color, mb: 0.5 }}>
                        {entry.name}: {entry.value.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}
                    </Typography>
                ))}
            </Box>
        );
    }
    return null;
};

const StatisticsSection = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('access_token');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const headers = {
                    'Content-Type': 'application/json',
                };
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                
                const response = await fetch(`${API_URL}/booking-statistics/`, { headers });
                const data = await response.json();
                setStats(data);
            } catch (error) {
                console.error('Error fetching statistics:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchStats();
    }, [token]);

    if (loading) {
        return <Box sx={{ p: 2 }}><CircularProgress /></Box>;
    }

    if (!stats) {
        return null;
    }

    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;

    return (
        <Box sx={{ mb: 4, width: '100%', maxWidth: '100%' }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                Statystyki
            </Typography>
            
            {/* Current Year Summary + Comparison — single row */}
            {(() => {
                const cyTotal = stats.current_year.total;
                const lyTotal = stats.last_year_total || { nights: 0, revenue: 0 };
                const revDiff = cyTotal.revenue - lyTotal.revenue;
                const nightsDiff = cyTotal.nights - lyTotal.nights;
                const revPct = lyTotal.revenue > 0 ? ((revDiff / lyTotal.revenue) * 100).toFixed(1) : null;
                const ahead = revDiff >= 0;
                const aptCols = (
                    <>
                        <TableCell sx={{ py: 0.5, px: 1 }}><strong>Apartament</strong></TableCell>
                        <TableCell align="right" sx={{ py: 0.5, px: 1 }}><strong>Doby</strong></TableCell>
                        <TableCell align="right" sx={{ py: 0.5, px: 1 }}><strong>Przychód</strong></TableCell>
                    </>
                );
                return (
                <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
                    <Grid item xs={12} md={2}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary">{currentYear} - Zrealizowane</Typography>
                                <Typography variant="h5" color="primary">{stats.current_year.realized.nights} dób</Typography>
                                <Typography variant="body2" color="text.secondary">{stats.current_year.realized.revenue.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary">{currentYear} - Do zrealizowania</Typography>
                                <Typography variant="h5" color="secondary">{stats.current_year.upcoming.nights} dób</Typography>
                                <Typography variant="body2" color="text.secondary">{stats.current_year.upcoming.revenue.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary">{currentYear} - Razem</Typography>
                                <Typography variant="h5" color="success.main">{stats.current_year.total.nights} dób</Typography>
                                <Typography variant="body2" color="text.secondary">{stats.current_year.total.revenue.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', border: `2px solid ${ahead ? '#4caf50' : '#f44336'}` }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                    Porównanie {currentYear} vs {lastYear}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 3, alignItems: 'baseline' }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{lastYear}</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{lyTotal.revenue.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })} · {lyTotal.nights} dób</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{currentYear}</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{cyTotal.revenue.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })} · {cyTotal.nights} dób</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {ahead ? <TrendingUpIcon color="success" fontSize="small" /> : <TrendingDownIcon color="error" fontSize="small" />}
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }} color={ahead ? 'success.main' : 'error.main'}>
                                            {revDiff >= 0 ? '+' : ''}{revDiff.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })} ({nightsDiff >= 0 ? '+' : ''}{nightsDiff} dób)
                                        </Typography>
                                    </Box>
                                    {revPct !== null && (
                                        <Chip
                                            icon={ahead ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                            label={`${revPct >= 0 ? '+' : ''}${revPct}% vs ${lastYear}`}
                                            color={ahead ? 'success' : 'error'}
                                            size="small"
                                        />
                                    )}
                                </Box>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>{currentYear} per apartament</Typography>
                                    <Table size="small">
                                        <TableHead><TableRow>{aptCols}</TableRow></TableHead>
                                        <TableBody>
                                            {(stats.current_year_stats || []).map(apt => (
                                                <TableRow key={apt.apartment_id}>
                                                    <TableCell sx={{ py: 0.5, px: 1 }}>{apt.apartment_name}</TableCell>
                                                    <TableCell align="right" sx={{ py: 0.5, px: 1 }}>{apt.total_nights}</TableCell>
                                                    <TableCell align="right" sx={{ py: 0.5, px: 1 }}>{apt.total_revenue.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>{lastYear} per apartament</Typography>
                                    <Table size="small">
                                        <TableHead><TableRow>{aptCols}</TableRow></TableHead>
                                        <TableBody>
                                            {stats.last_year_stats.map(apt => (
                                                <TableRow key={apt.apartment_id}>
                                                    <TableCell sx={{ py: 0.5, px: 1 }}>{apt.apartment_name}</TableCell>
                                                    <TableCell align="right" sx={{ py: 0.5, px: 1 }}>{apt.total_nights}</TableCell>
                                                    <TableCell align="right" sx={{ py: 0.5, px: 1 }}>{apt.total_revenue.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    </Grid>
                </Grid>
                );
            })()}

            {/* Monthly Comparison Charts - All in one row */}
            <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom>
                                Porównanie dób - {currentYear} vs {lastYear}
                            </Typography>
                            <Box sx={{ flexGrow: 1, width: '100%', minHeight: 0 }}>
                                <ResponsiveContainer width="99%" height="100%" minHeight={300}>
                                <BarChart data={stats.monthly_comparison}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month_name" />
                                    <YAxis />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey="last_year.nights" fill="#8884d8" name={`${lastYear} doby`} />
                                    <Bar dataKey="current_year.realized.nights" fill="#82ca9d" name={`${currentYear} zrealizowane`} />
                                    <Bar dataKey="current_year.upcoming.nights" fill="#ffc658" name={`${currentYear} przyszłe`} />
                                </BarChart>
                            </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom>
                                Porównanie przychodów - {currentYear} vs {lastYear}
                            </Typography>
                            <Box sx={{ flexGrow: 1, width: '100%', minHeight: 0 }}>
                                <ResponsiveContainer width="99%" height="100%" minHeight={300}>
                                <LineChart data={stats.monthly_comparison}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month_name" />
                                    <YAxis />
                                    <Tooltip content={<CustomCurrencyTooltip />} />
                                    <Legend />
                                    <Line type="monotone" dataKey="last_year.revenue" stroke="#8884d8" name={`${lastYear} przychód`} />
                                    <Line type="monotone" dataKey="current_year.realized.revenue" stroke="#82ca9d" name={`${currentYear} zrealizowane`} />
                                    <Line type="monotone" dataKey="current_year.upcoming.revenue" stroke="#ffc658" name={`${currentYear} przyszłe`} />
                                </LineChart>
                            </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom>
                                Przychody za miesiąc - {currentYear} vs {lastYear}
                            </Typography>
                            <Box sx={{ flexGrow: 1, width: '100%', minHeight: 0 }}>
                                <ResponsiveContainer width="99%" height="100%" minHeight={300}>
                                <BarChart 
                                    data={stats.monthly_comparison.map(month => ({
                                        ...month,
                                        current_year_total: month.current_year.realized.revenue + month.current_year.upcoming.revenue
                                    }))}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month_name" />
                                    <YAxis />
                                    <Tooltip content={<CustomCurrencyTooltip />} />
                                    <Legend />
                                    <Bar dataKey="last_year.revenue" fill="#8884d8" name={`${lastYear} przychód`} />
                                    <Bar dataKey="current_year_total" fill="#82ca9d" name={`${currentYear} przychód`} />
                                </BarChart>
                            </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

const Dashboard = () => (
    <div style={{ width: '100%', maxWidth: '100%' }}>
        <DashboardSummary />
        <StatisticsSection />
        <BookingFutureList />
    </div>
);

export default Dashboard; 